import {
	Bone,
	LoadAssetContainerAsync,
	Mesh,
	Scene,
	Skeleton,
	StandardMaterial,
	Vector3,
} from '@babylonjs/core';
import * as helpers from '../helpers';
import { TOpening, TPorchConfig, TSize } from '../helpers/type';
import { SeadevShedBuilder } from '../ssb_builder';
import { COMPONENT_SIZE_FULL_WALL, constants, zSide, PORCH_PRESET } from '../ssb_constants';
import {
	mainSidePlaceDoor,
	switchPorchConfig,
	sidesPorchConfig,
	skeletonMapping,
	switchPorchScaleColumn,
	internalPorchConfigs,
	allSidesPorch,
	mainSideColumn,
	fullWallComponentMapping,
} from './config';
import {
	adjustBalusterPoints,
	applyMaterial,
	calculateBraceTransform,
	calculateComponentQuantity,
	cloneMeshAtPositions,
	createFaceUV,
	disposeMeshes,
	findValidNewSideForComponent,
	getDataCornerTrimPorch,
	getDataDrawWallByType,
	getDataDrawWallPorch,
	getDistanceSettingsForSide,
	getDoorInMainSideOldPorch,
	getOpeningConfigForSide,
	getSideBoundaryPoints,
	handleMoveBoneAddPorch,
	isSideFixedPosition,
	prepareDataDrawWall,
	setColumnInstancesMetadata,
	transformOpeningSettingToColumnPositions,
	swapMaterials,
	transformStartPointCloneBaluster,
	isEmptyComponentFullWallOnSide,
	getSidesFlipModelOption,
	convertSides,
	getFlippedPositionForComponent,
	getAnchorPosition,
	isPorchRailingOff,
	resolveOpeningToFeet,
} from './helper';
export class AddPorchModule {
	private PORCH_STYLE_NONE = 'none';
	public scene: Scene;
	public shedBuilder: SeadevShedBuilder;
	public ALL_COMPONENTS = [];
	public CDN_PATH = '';
	public originWallData = [];
	public originWallDataInterior = [];
	public originCornerTrimData = [];
	public currentRail: {
		style?: string;
		offset_floor?: number;
		baluster_style?: string;
		distance?: number;
		offset_edge?: number;
		height?: number;
	} = {};
	public currentBaluster: {} = {};
	public currentColumn: { style?: string; config?: any } = {};
	public currentBrace: { style?: string; offset_top?: number } = {};
	public currentPorchStyle = this.PORCH_STYLE_NONE;
	public currentPorchStyleID: string = '';
	public currentPorchConfig: TPorchConfig = {
		id: '',
		style: 'none',
		depth: 0,
		width: 0,
		alley_width: 0,
		side_wall_width: 0,
		diagonal_width: 0,
		diagonal_depth: 0,
		position: 'left',
	};
	public currentSize: TSize;
	public deckWallMesh: Mesh[] = [];
	public ceilingWallMesh: Mesh[] = [];
	public currentOpening: any;
	public componentMeshes: any = {
		rail: [],
		baluster: [],
		column: [],
		brace: [],
	};
	public porchRoofShape: string = '';
	public isFirstDrawWall: boolean = true;

	public postDistanceRules = [
		{
			sides: ['front', 'right', 'left', 'back'],
			post_distance: 4,
			max_distance: 100,
			porch_styles: [],
		},
	];

	public oldPorchConfig: any = {
		position: 'left',
		style: 'none',
	};

	public COMPONENT_CONFIGS;
	constructor(shedBuilder) {
		/**
		 * Initializes the AddPorchModule with the provided shed builder instance.
		 * Sets up the scene, CDN path, loads component configurations, and initializes data.
		 * @param {SeadevShedBuilder} shedBuilder - The main shed builder instance.
		 */
		this.scene = shedBuilder._scene;
		this.shedBuilder = shedBuilder;
		this.CDN_PATH = this.shedBuilder.getGlobalData(constants.cndURL) + '/component/porch/';
		this.COMPONENT_CONFIGS = shedBuilder.getSettingData('porch_components');
		this.porchRoofShape = shedBuilder.getRoofShape();
		const postDistanceRule = shedBuilder.getSettingData('porch_post_distance_rule');
		if (postDistanceRule) {
			this.postDistanceRules = postDistanceRule;
		}
		this.loadPorchComponents(this.CDN_PATH, this.COMPONENT_CONFIGS);
		this.createSkeleton();
	}

	//Setter function
	/**
	 * Sets the data for drawing walls based on the porch style.
	 * If porch style is 'none', uses original wall data; otherwise, prepares porch wall data.
	 * @param {string} porchStyle - The style of the porch ('none', 'front', 'side', etc.).
	 */
	setDataDrawWall = (porchStyle, porchConfig) => {
		let originWallData = this.originWallData;
		if (porchStyle === this.PORCH_STYLE_NONE) {
			this.shedBuilder._apiData[constants.keyWallData] = originWallData;
		} else {
			const roofShape =
				this.porchRoofShape === 'gambrel' ||
				this.porchRoofShape === 'gambrel_accurate_measurements'
					? 'gambrel'
					: 'aframe';
			const position = porchConfig?.position || 'left';
			const wallPorch = getDataDrawWallPorch(porchStyle, roofShape, position);
			if (porchStyle === 'middle') {
				originWallData = originWallData.filter((item) => item.side !== 'left');
			}
			this.shedBuilder._apiData[constants.keyWallData] = prepareDataDrawWall(
				wallPorch,
				originWallData,
			);
		}
	};

	/**
	 * Sets the data for drawing walls based on the porch style.
	 * If porch style is 'none', uses original wall data; otherwise, prepares porch wall data.
	 * @param {string} porchStyle - The style of the porch ('none', 'front', 'side', etc.).
	 */
	setDataDrawWallInterior = (porchStyle, porchConfig) => {
		let originWallData = this.originWallDataInterior;
		if (porchStyle === this.PORCH_STYLE_NONE) {
			this.shedBuilder._apiData[constants.keyWallDataInterior] = originWallData;
		} else {
			const roofShape =
				this.porchRoofShape === 'gambrel' ||
				this.porchRoofShape === 'gambrel_accurate_measurements'
					? 'gambrel'
					: 'aframe';
			const position = porchConfig?.position || 'left';
			if (porchStyle === 'middle') {
				originWallData = originWallData.filter((item) => item.side !== 'left');
			}
			let wallPorch = getDataDrawWallPorch(porchStyle, roofShape, position);
			wallPorch = wallPorch.filter((item) => item.side !== 'floor');
			this.shedBuilder._apiData[constants.keyWallDataInterior] = prepareDataDrawWall(
				wallPorch,
				originWallData,
			);
		}
	};

	/**
	 * Sets the data for corner trim based on the porch style.
	 * If porch style is 'none', uses original corner trim data; otherwise, gets porch-specific corner trim data.
	 * @param {string} porchStyle - The style of the porch ('none', 'front', 'side', etc.).
	 */
	setDataCornerTrim = (porchStyle, porchConfig) => {
		const originCornerTrimData = this.originCornerTrimData;
		if (porchStyle === this.PORCH_STYLE_NONE) {
			this.shedBuilder._apiData[constants.keyCornerTrimData] = originCornerTrimData;
		} else {
			const position = porchConfig?.position || 'left';
			this.shedBuilder._apiData[constants.keyCornerTrimData] = getDataCornerTrimPorch(
				porchStyle,
				position,
			);
		}
	};

	/**
	 * Sets the current rail configuration.
	 * @param {object} data - The rail data including style, offsetFloor, and height.
	 */
	setCurrentRail = (data) => {
		this.currentRail = data;
	};

	/**
	 * Sets the current baluster configuration.
	 * @param {object} data - The baluster data including style, distance, and edgeOffset.
	 */
	setCurrentBaluster = (data) => {
		this.currentBaluster = data;
	};

	/**
	 * Sets the current brace configuration.
	 * @param {object} data - The brace data including style, distance, and edgeOffset.
	 */
	setCurrentBrace = (data) => {
		this.currentBrace = data;
	};

	/**
	 * Sets the current column configuration.
	 * @param {object} data - The column data including style and config.
	 */
	setCurrentColumn = (data) => {
		this.currentColumn = data;
	};

	/**
	 * Sets the current size of the shed.
	 * @param {object} data - The size data including baseWidth, baseDepth, baseHeight.
	 */
	setCurrentSize = (data: TSize) => {
		this.currentSize = data;
	};

	/**
	 * Sets the current porch style.
	 * @param {string} style - The porch style ('none', 'front', 'side', etc.).
	 */
	setCurrentPorchStyle = (style: string) => {
		this.currentPorchStyle = style;
	};

	/**
	 * Sets the current porch ID.
	 * @param {string} id - The unique identifier for the porch.
	 */
	setCurrentPorchStyleID = (id: string) => {
		this.currentPorchStyleID = id;
	};

	/**
	 * Sets the data for the current opening.
	 * @param {TOpening} data - The opening data.
	 */
	setOpeningData = (data: any) => {
		this.currentOpening = data;
	};

	/**
	 * Sets the origin data for drawing walls if not already set.
	 * @param {Array} data - The original wall data.
	 */
	setOriginDataDrawWall = (data) => {
		if (this.originWallData.length === 0) {
			this.originWallData = data;
		}
	};

	/**
	 * Sets the origin data for drawing walls if not already set.
	 * @param {Array} data - The original wall data.
	 */
	setOriginDataDrawWallInterior = (data) => {
		if (this.originWallDataInterior.length === 0) {
			this.originWallDataInterior = data;
		}
	};

	/**
	 * Sets the origin data for corner trim if not already set.
	 * @param {Array} data - The original corner trim data.
	 */
	setOriginDataCornerTrim = (data) => {
		if (this.originCornerTrimData.length === 0) {
			this.originCornerTrimData = data;
		}
	};

	getCurrentPorchConfig = () => {
		return {
			id: this.currentPorchConfig?.id,
			style: this.currentPorchConfig?.style,
			depth: this.currentPorchConfig?.depth,
			width: this.currentPorchConfig?.width,
			alleyWidth: this.currentPorchConfig?.alley_width,
			sideWallWidth: this.currentPorchConfig?.side_wall_width,
			diagonalWidth: this.currentPorchConfig?.diagonal_width,
			diagonalDepth: this.currentPorchConfig?.diagonal_depth,
			position: this.currentPorchConfig?.position,
		};
	};

	/**
	 * Gets the current size of the shed.
	 * @returns {object} The current size data.
	 */
	getCurrentSize = () => {
		return this.currentSize;
	};

	/**
	 * Get the current opening configuration.
	 */
	getCurrentOpening = () => {
		return this.currentOpening;
	};

	/**
	 * Gets the mesh name by type and style.
	 * @param {string} type - The component type (e.g., 'rail', 'column').
	 * @param {string} style - The style of the component.
	 * @returns {string} The mesh name.
	 */
	getMeshNameByTypeStyle = (type, style) => {
		return this.ALL_COMPONENTS[type + '_' + style]?.meshName || null;
	};

	/**
	 * Gets the mesh name by type and style.
	 * @param {string} type - The component type (e.g., 'rail', 'column').
	 * @param {string} style - The style of the component.
	 * @returns {string} The mesh name.
	 */
	getMeshByTypeStyle = (type, style) => {
		return this.ALL_COMPONENTS[type + '_' + style]?.mesh || null;
	};

	/**
	 * Retrieves a mesh by its name from the scene.
	 * @param {string} name - The name of the mesh.
	 * @returns {Mesh} The mesh object.
	 */
	getMeshByName = (name) => {
		return this.scene.getMeshByName(name) as Mesh;
	};

	/**
	 * Gets the point placement data by porch style and type.
	 * @param {string} porchStyle - The porch style.
	 * @returns {object} The point placement data.
	 */
	// getPointPlaceByType = (porchStyle) => {
	// 	if (this.shedBuilder.isPorchOptions()) {
	// 		return switchPorchConfig[porchStyle] || null;
	// 	}

	// 	const configs = internalPorchConfigs?.[this.shedBuilder._shedType]?.configs || null;

	// 	const results = {};
	// 	Object.entries(configs).forEach(([side, data]) => {
	// 		const startBone = data['start']['bone'];
	// 		const endBone = data['end']['bone'];
	// 		const startOffset = data['start']['offset'];
	// 		const endOffset = data['end']['offset'];

	// 		console.log('side: ', side);
	// 		console.log('data: ', data);
	// 		console.log('startBone: ', startBone);
	// 		console.log('endBone: ', endBone);
	// 		console.log('startOffset: ', startOffset);
	// 		console.log('endOffset: ', endOffset);

	// 		results[side]['start'] = this.shedBuilder._bones[startBone].position.add(startOffset);
	// 		results[side]['end'] = this.shedBuilder._bones[endBone].position.add(endOffset);
	// 	});

	// 	console.log('results: ', results);

	// 	return results;
	// };

	/**
	 * Gets the side configs (start/end bones per physical side) by porch style and position.
	 * Corner and L-shape configs are nested by position (left/right) first.
	 * @param {string} porchStyle - The porch style.
	 * @param {string} porchPosition - The porch position Left/Right.
	 * @returns {object|null} Map of side => { start, end } or null when unknown.
	 */
	getSideConfigsByType = (porchStyle: string, porchPosition: string) => {
		if (!this.shedBuilder.isPorchOptions()) {
			return internalPorchConfigs?.[this.shedBuilder._shedType]?.configs || null;
		}
		switch (porchStyle) {
			case 'front':
			case 'side':
			case 'middle':
				return switchPorchConfig?.[porchStyle] || null;
			case 'corner':
			case 'l_shape':
				return switchPorchConfig?.[porchStyle]?.[porchPosition] || null;
			default:
				return null;
		}
	};

	/**
	 * Gets the physical sides (front/left/right/back) a porch style exposes at a position.
	 * Pure config lookup, so it is safe to call before the model/bones are loaded.
	 * @param {string} porchStyle - The porch style.
	 * @param {string} porchPosition - The porch position Left/Right.
	 * @returns {string[]} List of side names.
	 */
	getPhysicalSides = (porchStyle: string, porchPosition: string): string[] => {
		const configs = this.getSideConfigsByType(porchStyle, porchPosition);
		return configs ? Object.keys(configs) : [];
	};

	/**
	 * Gets the point placement data by porch style and type.
	 * @param {string} porchStyle - The porch style.
	 * @param {string} porchPosition - The porch position Left/Right.
	 * @returns {object} The point placement data.
	 */
	getPointPlaceByType = (porchStyle, porchPosition) => {
		const configs: any = this.getSideConfigsByType(porchStyle, porchPosition);
		if (!configs) {
			return null;
		}
		const results = {};
		Object.entries(configs).forEach(([side, data]) => {
			// Extract bone name depending on whether it is an object or a string
			const startBone = data['start']['bone'];
			const endBone = data['end']['bone'];

			const startOffset = data['start']['offset'];
			const endOffset = data['end']['offset'];

			const startVector3 = this.shedBuilder._bones[startBone];
			const endVector3 = this.shedBuilder._bones[endBone];

			if (!startVector3 || !endVector3) {
				console.warn(
					`[Porch] getPointPlaceByType: missing bone "${startVector3}" or "${endVector3}" for side "${side}"`,
				);
				return;
			}

			// Resolve bone names to Vector3 positions and apply offsets
			results[side] = {
				start: startVector3.position.add(startOffset),
				end: endVector3.position.add(endOffset),
			};
		});

		return results;
	};

	/**
	 * Get scale column points based on porch style.
	 * @returns {object} The scale column points.
	 */
	getScaleColumnPoints = (): { top: Vector3; bottom: Vector3 } => {
		const config = this.shedBuilder.isPorchOptions()
			? switchPorchScaleColumn
			: internalPorchConfigs?.[this.shedBuilder._shedType]?.scaleColumn || null;

		let postOffset = this.shedBuilder.getSettingData('post_offset') || 0;
		postOffset = isNaN(postOffset) ? 0 : Number(postOffset) / 12;

		let top = this.shedBuilder._bones[config['top']['bone']].position;
		const offsetTop = new Vector3(0, postOffset, 0);
		top = top.add(offsetTop);

		let bottom = this.shedBuilder._bones[config['bottom']['bone']].position;
		const offsetBottom = config['bottom']['offset'];
		bottom = bottom.add(offsetBottom);

		return {
			top,
			bottom,
		};
	};

	/**
	 * Gets the list of available columns based on configurations.
	 * Filters column configurations that match the given conditions.
	 * @returns {Array} Array of available column configurations per side.
	 */
	getPostDistanceRule = () => {
		return this.postDistanceRules;
	};

	/**
	 * Asynchronously loads a mesh container for a specific type and style.
	 * @param {string} type - The component type.
	 * @param {string} style - The style of the component.
	 * @param {string} url - The URL to load the asset container from.
	 */
	loadMeshContainerAsync = async (type, style, url) => {
		try {
			const container = await LoadAssetContainerAsync(url, this.scene);
			this.ALL_COMPONENTS[type + '_' + style] = {
				isAddedScene: false,
				container: container,
				meshName: container.meshes[0].name,
				mesh: container.meshes[0],
			};
		} catch (e) {
			console.error(`Failed to ${url}:`, e);
		}
	};

	/**
	 * Handles adding meshes to the scene for a specific type and style if not already added.
	 * @param {string} type - The component type.
	 * @param {string} style - The style of the component.
	 */
	handleMeshesToScene = (type, style) => {
		const isAdded = this.ALL_COMPONENTS[type + '_' + style]?.isAddedScene;
		if (isAdded == false) {
			const container = this.ALL_COMPONENTS[type + '_' + style]?.container;
			this.addComponentsToScene(container);
			this.ALL_COMPONENTS[type + '_' + style].isAddedScene = true;
		}
	};

	/**
	 * Adds all meshes from the container to the scene and applies initial UVs and specular power.
	 * @param {AssetContainer} container - The asset container to add to the scene.
	 */
	addComponentsToScene = (container) => {
		const _this = this;
		container.addAllToScene();
		container.meshes.forEach((mesh: Mesh) => {
			let material = mesh.material as StandardMaterial;
			if (material != null) {
				_this.shedBuilder.setInitialUVs(material);
				_this.shedBuilder.setSpecularPower(material);
				swapMaterials(material);
				if (material['subMaterials'] != undefined) {
					let subMaterials = material['subMaterials'];
					subMaterials.forEach((subMaterial) => {
						_this.shedBuilder.setInitialUVs(subMaterial);
						_this.shedBuilder.setSpecularPower(subMaterial);
						swapMaterials(subMaterial);
					});
				}
			}
		});
		container.lights.forEach((l) => {
			l.dispose();
		});
	};

	/**
	 * Loads all porch components asynchronously from the CDN.
	 * @param {string} cnd - The CDN base URL.
	 * @param {object} componentConfigs - The component configurations.
	 */
	loadPorchComponents = async (cnd, componentConfigs) => {
		const tasks = [];
		Object.entries(componentConfigs).forEach(([type, configs]) => {
			if (!Array.isArray(configs)) return;
			configs.forEach((config, index) => {
				const style = config['style'];
				const url = cnd + `${style}/${style}.babylon`;
				tasks.push(this.loadMeshContainerAsync(type, style, url));
			});
		});

		await Promise.all(tasks);
	};

	/**
	 * Changes the porch configuration based on the provided data.
	 * Updates bones, wall data, builds mesh, handles components, and draws ceiling/deck.
	 * @param {object} data - The porch configuration data including porchID, porchStyle, dimensions, materials, etc.
	 */
	changePorch = async (data) => {
		const {
			baseWidth,
			baseDepth,
			baseHeight,
			deckMaterial,
			ceilingMaterial,
			deckColor,
			porchConfig,
			opening,
			isSwapComponent,
		} = data;

		this.oldPorchConfig = this.currentPorchConfig;
		const bones = this.shedBuilder._bones;
		const porchStyle = porchConfig?.style || this.PORCH_STYLE_NONE;
		this.shedBuilder._currentPorchType = porchStyle;
		this.currentPorchConfig = porchConfig;
		// Step 1: Remove old porch elements from the scene
		this.removeOldWallsPorch();
		this.removeCornersTrimPorch();
		this.removeCeilingAndDeck();
		this.removeInstanceComponentMeshes();
		if (this.isFirstDrawWall) {
			await this.shedBuilder.buildMesh();
			this.isFirstDrawWall = false;
		}
		this.removeAllWallDefault();
		// Step 2: Update current porch style, size, and ID
		this.setCurrentPorchStyle(porchStyle);
		this.setCurrentSize({ baseWidth, baseDepth, baseHeight });
		this.setCurrentPorchStyleID(porchConfig?.porch_style_id);
		this.setOriginDataCornerTrim(this.shedBuilder._apiData[constants.keyCornerTrimData]);
		this.setDataDrawWall(porchStyle, porchConfig);
		this.setDataDrawWallInterior(porchStyle, porchConfig);
		this.setDataCornerTrim(porchStyle, porchConfig);
		this.setOpeningData(opening);
		this.shedBuilder.setPorchWidth(porchConfig?.width);
		this.shedBuilder.setPorchDepth(porchConfig?.depth);

		// Step 3: Move bones to position porch elements correctly based on the new configuration
		handleMoveBoneAddPorch({ porchConfig, baseWidth, baseDepth, baseHeight, bones });
		// Step 4: Rebuild the mesh and handle components based on the new porch configuration
		await this.shedBuilder.buildMesh();
		this.handleUpdateFrame();
		const cornerTrimMesh = this.shedBuilder.getGlobalData(constants.keyCornerTrimMesh);
		this.shedBuilder.handleCornerTrims(cornerTrimMesh);

		if (isSwapComponent) {
			const allComponents = this.shedBuilder.getAllComponents();
			this.handleSwapComponent(allComponents, this.oldPorchConfig, this.currentPorchConfig);
		}

		// Step 5: If porch style is not 'none', draw ceiling and deck, and handle columns, railings, balusters, and braces based on the opening configuration
		if (porchStyle === this.PORCH_STYLE_NONE) return;
		this.drawCeilingWall(ceilingMaterial);
		this.drawDeckWall(deckMaterial, deckColor);
		const availableColumn = this.getAvailablePostDistanceRule(porchConfig?.porch_style_id);
		this.handleColumn(opening);
		this.handleRailing(opening, availableColumn);
		this.handleBaluster(opening, availableColumn);
		this.handleBrace();
		this.initColorPostRail();
	};

	/**
	 * Removes all instance component meshes for rail, baluster, and column.
	 */
	removeInstanceComponentMeshes = () => {
		this.removeComponentMeshes('rail');
		this.removeComponentMeshes('baluster');
		this.removeComponentMeshes('column');
		this.removeComponentMeshes('brace');
	};

	/**
	 * Handles updating the frame based on the selected upgrade option.
	 */
	handleUpdateFrame = () => {
		const selected = jQuery(`input[name="ssb-configurator-upgrade-frame"]:checked`);
		if (0 === selected.length) return;
		const file = selected.data('file');
		const distributionRules = JSON.parse(window.atob(selected.data('distribution-rules')));

		if ('in-model' === file) {
			this.shedBuilder.cloneFrameInModelObject(distributionRules);
		} else {
			if (file) {
				this.shedBuilder.cloneFrameComponent(file, distributionRules, false);
			}
		}
	};

	/**
	 * Handles opening configuration for columns, railings, and balusters.
	 *
	 * @param openings Object opening data
	 */
	handleOpening = (openings) => {
		this.setOpeningData(openings);
		const availableColumn = this.getAvailablePostDistanceRule(this.currentPorchStyleID);
		this.removeInstanceComponentMeshes();
		this.handleColumn(openings);
		this.handleRailing(openings, availableColumn);
		this.handleBaluster(openings, availableColumn);
		this.handleBrace();
	};

	/**
	 * Removes all meshes of a specific component type.
	 * @param {string} type - The type of component to remove ('rail', 'baluster', 'column').
	 */
	removeComponentMeshes = (type) => {
		const listMeshes = this.componentMeshes[type];
		if (listMeshes.length) {
			for (let i = 0; i < listMeshes.length; i++) {
				listMeshes[i].dispose();
			}
			this.componentMeshes[type] = [];
		}
	};

	/**
	 * Handles the creation and placement of railing meshes based on opening configuration and available columns.
	 * Creates instances of railing meshes for each side, adjusting for openings and scaling appropriately.
	 * @param {object} opening - The opening configuration with position, size, and sides.
	 * @param {Array} availableColumn - Array of available column configurations per side.
	 */
	handleRailing = (opening: any, availableColumn: any, activeSides?: string[]) => {
		const currentRailStyle = this.currentRail.style;
		const railOffsetFloor = this.currentRail.offset_floor / 12;
		const balusterHeight = this.currentRail.height / 12;
		const porchPosition = this.currentPorchConfig?.position || 'left';
		this.handleMeshesToScene('rail', currentRailStyle);
		this.removeComponentMeshes('rail');
		const railingName = this.getMeshNameByTypeStyle('rail', currentRailStyle);
		const railingMesh = this.getMeshByTypeStyle('rail', currentRailStyle);
		if (!railingMesh || isPorchRailingOff()) return;

		const railingWidth = helpers.getMeshWidth(railingMesh);
		const railingHeight = helpers.getMeshHeight(railingMesh);
		const pointPlaces = this.getPointPlaceByType(this.currentPorchStyle, porchPosition);
		const listMeshes = [];
		const currentColumnStyle = this.currentColumn.style;
		const columnName = this.getMeshNameByTypeStyle('column', currentColumnStyle);
		const columnMesh = this.getMeshByName(columnName);
		const columnWidth = helpers.getMeshWidth(columnMesh);
		if (!pointPlaces) return;
		// Step 1: Process each side of the porch

		if (!pointPlaces) return;

		Object.entries(pointPlaces).forEach(([side, data]) => {
			if (activeSides !== undefined && !activeSides.includes(side)) {
				return;
			}
			// Step 2: Get distance settings for this side from available columns
			const { distanceColumn, maxDistanceColumn } = getDistanceSettingsForSide(
				side,
				availableColumn,
			);

			// Step 3: Get opening configuration for this side
			const { openingSize, openingPosition, openingOffset } = getOpeningConfigForSide(
				opening,
				side,
			);

			// Step 4: Get start and end points for this side
			const pointStart = data['start'];
			const pointEnd = data['end'];
			const rotation =
				side === 'left' || side === 'right'
					? new Vector3(0, Math.PI / 2, 0)
					: new Vector3(0, 0, 0);

			// Step 5: Calculate quantity based on distance rules
			const totalDistance = Vector3.Distance(pointStart, pointEnd);
			const quantity = calculateComponentQuantity(
				totalDistance,
				distanceColumn,
				maxDistanceColumn,
				openingSize,
			);

			if (
				quantity == 0 ||
				(!openingPosition && mainSideColumn[this.currentPorchStyle] === side)
			) {
				return;
			}

			// Step 6: Transform points based on column width and side
			const halfColumnWidth: number = columnWidth / 2;
			const transformPoint = getSideBoundaryPoints({
				startPoint: pointStart,
				endPoint: pointEnd,
				side,
				halfColumnWidth,
				porchStyle: this.currentPorchStyle,
				porchPosition,
			});

			// Step 7: Get segments for railing placement
			let { segments } = transformOpeningSettingToColumnPositions({
				opening,
				columnWidth,
				side,
				quantity,
				startPoint: transformPoint.startPoint,
				endPoint: transformPoint.endPoint,
				openingSize,
				openingPosition,
				offset: openingOffset,
				maxDistance: maxDistanceColumn,
				distanceColumn,
				originStartPoint: pointStart,
				originEndPoint: pointEnd,
			});

			segments = segments.filter((i) => i.type === 'rail');

			// Step 8: Create and position rail instances for each segment
			segments.forEach((segment, index) => {
				const distance = Vector3.Distance(segment.start, segment.end);
				const centerPoint = Vector3.Center(segment.start, segment.end);
				const suffix = segment['suffix'] || '';
				const cloneRailBottom = railingMesh.createInstance(
					`${railingName}_clone_${side}${suffix}_bottom`,
				);
				const cloneRailTop = railingMesh.createInstance(
					`${railingName}_clone_${side}${suffix}_top`,
				);
				cloneRailBottom.scaling.x = distance / railingWidth;
				cloneRailTop.scaling.x = distance / railingWidth;
				cloneRailBottom.position = new Vector3(
					centerPoint.x,
					centerPoint.y + railingHeight / 2 + railOffsetFloor,
					centerPoint.z,
				);
				cloneRailTop.position = new Vector3(
					centerPoint.x,
					centerPoint.y + railingHeight / 2 + railOffsetFloor + balusterHeight,
					centerPoint.z,
				);
				cloneRailBottom.rotation = rotation;
				cloneRailTop.rotation = rotation;
				listMeshes.push(cloneRailBottom, cloneRailTop);
			});
		});
		this.componentMeshes['rail'] = listMeshes;
		railingMesh.setEnabled(false);
	};

	/**
	 * Handles the creation and placement of baluster meshes based on opening configuration and available columns.
	 * Creates instances of baluster meshes spaced appropriately along each side.
	 * @param {object} opening - The opening configuration with position, size, and sides.
	 * @param {Array} availableColumn - Array of available column configurations per side.
	 */
	handleBaluster = (opening: any, availableColumn: any, activeSides?: string[]) => {
		const currentRailingStyle = this.currentRail.style;
		const currentBalusterStyle = this.currentRail.baluster_style;
		const distanceSettings = this.currentRail.distance;
		const edgOffsetSettings = this.currentRail.offset_edge / 12;
		const heightSettings = this.currentRail.height / 12;
		const railOffsetFloor = this.currentRail.offset_floor / 12;
		const porchPosition = this.currentPorchConfig?.position || 'left';

		this.handleMeshesToScene('baluster', currentBalusterStyle);
		this.removeComponentMeshes('baluster');
		const balusterName = this.getMeshNameByTypeStyle('baluster', currentBalusterStyle);
		const balusterMesh = this.getMeshByTypeStyle('baluster', currentBalusterStyle);
		const railMesh = this.getMeshByTypeStyle('rail', currentRailingStyle);

		if (!balusterMesh || isPorchRailingOff()) return;

		// Get rail mesh dimensions
		let railMeshHeight = 0;
		if (railMesh) {
			railMeshHeight = helpers.getMeshHeight(railMesh);
		}

		// Scale baluster mesh based on height settings
		const balusterHeight = helpers.getMeshHeight(balusterMesh);
		const balusterLength = helpers.getMeshLength(balusterMesh);
		balusterMesh.scaling.y = heightSettings / balusterHeight;

		const pointPlaces = this.getPointPlaceByType(this.currentPorchStyle, porchPosition);

		if (!pointPlaces) return;

		const listMeshes = [];

		const currentColumnStyle = this.currentColumn.style;
		const columnName = this.getMeshNameByTypeStyle('column', currentColumnStyle);
		const columnMesh = this.getMeshByName(columnName);
		const columnWidth = helpers.getMeshWidth(columnMesh);
		if (!pointPlaces) return;
		// Step 1: Process each side of the porch
		Object.entries(pointPlaces).forEach(([side, data]) => {
			if (activeSides !== undefined && !activeSides.includes(side)) {
				return;
			}
			// Step 2: Get distance settings for this side
			const { distanceColumn, maxDistanceColumn } = getDistanceSettingsForSide(
				side,
				availableColumn,
			);

			// Step 3: Calculate offset from edge and position
			const offsetZ = edgOffsetSettings - balusterLength / 2;
			const offsetX = 0;
			// Step 4: Get start and end points for this side
			const startPoint = data['start'];
			const endPoint = data['end'];

			// Step 5: Get opening configuration for this side
			const { openingSize, openingPosition, openingOffset } = getOpeningConfigForSide(
				opening,
				side,
			);

			// Step 6: Calculate quantity based on distance rules
			const totalDistance = Vector3.Distance(startPoint, endPoint);
			const quantity = calculateComponentQuantity(
				totalDistance,
				distanceColumn,
				maxDistanceColumn,
				openingSize,
			);

			if (
				quantity == 0 ||
				(!openingPosition && mainSideColumn[this.currentPorchStyle] === side)
			) {
				return;
			}

			// Step 7: Transform points based on column width and side
			const halfColumnWidth: number = columnWidth / 2;
			const transformPoint = getSideBoundaryPoints({
				startPoint,
				endPoint,
				side,
				halfColumnWidth: halfColumnWidth,
				porchStyle: this.currentPorchStyle,
				porchPosition,
				// type: 'baluster',
			});

			// Step 8: Get segments for baluster placement
			let { segments } = transformOpeningSettingToColumnPositions({
				opening,
				columnWidth,
				side,
				quantity,
				startPoint: transformPoint.startPoint,
				endPoint: transformPoint.endPoint,
				openingSize,
				openingPosition,
				offset: openingOffset,
				maxDistance: maxDistanceColumn,
				distanceColumn,
				originStartPoint: startPoint,
				originEndPoint: endPoint,
			});

			segments = segments.filter((i) => i.type === 'rail');

			// Step 9: Create and position baluster instances for each segment
			segments.forEach((segment, index) => {
				const suffix = segment['suffix'] || `_${index}`;

				// Adjust points based on side and offsets
				const adjustedPoints = adjustBalusterPoints({
					side,
					startPoint: segment.start,
					endPoint: segment.end,
					offsetX,
					offsetZ,
					railOffsetFloor,
					railMeshHeight,
				});

				// const newStartPoint = transformStartPointCloneBaluster({
				// 	point: adjustedPoints.startPoint,
				// 	porchStyle: this.currentPorchStyle,
				// 	side,
				// 	halfColumnWidth,
				// 	isAdjacentWall: index === 0,
				// });

				// Create baluster instances along the segment
				const args = {
					meshClone: balusterMesh,
					meshName: balusterName,
					startPoint: adjustedPoints.startPoint,
					endPoint: adjustedPoints.endPoint,
					space: distanceSettings,
					parentName: '',
					cloneFull: true,
					scene: this.scene,
					meshInstanceName: `${balusterName}_${side}${suffix}`,
				};
				const instances = helpers.createInstanceMesh(args);
				listMeshes.push(...instances);
			});
		});
		this.componentMeshes['baluster'] = listMeshes;
		balusterMesh.setEnabled(false);
	};

	/**
	 * Gets the list of available columns based on configurations.
	 * Filters column configurations that match the given conditions.
	 * @returns {Array} Array of available column configurations per side.
	 */
	getAvailablePostDistanceRule = (porchStyleID) => {
		const result = [];
		this.postDistanceRules.forEach((rule) => {
			const porchStyles = rule.porch_styles;
			const isMatch =
				!porchStyles ||
				porchStyles.length === 0 ||
				porchStyles.includes(porchStyleID) ||
				!porchStyleID;
			if (isMatch) {
				result.push(rule);
			}
		});
		return result;
	};

	/**
	 * Handles the creation and placement of column meshes based on size and opening configuration.
	 * Creates instances of column meshes, scaling and positioning them appropriately for each side.
	 * @param {object} opening - The opening configuration.
	 */
	handleColumn = (opening: any) => {
		const currentColumnStyle = this.currentColumn.style;
		const porchPosition = this.currentPorchConfig?.position || 'left';
		this.handleMeshesToScene('column', currentColumnStyle);
		this.removeComponentMeshes('column');
		const columnName = this.getMeshNameByTypeStyle('column', currentColumnStyle);
		const columnMesh = this.getMeshByTypeStyle('column', currentColumnStyle);
		const deckThickness = 0.1;
		if (!columnMesh) return;

		const columnWidth = helpers.getMeshWidth(columnMesh);
		const columnHeight = helpers.getMeshHeight(columnMesh);

		const pointPlaces = this.getPointPlaceByType(this.currentPorchStyle, porchPosition);

		const { top: scalePointTop, bottom: scalePointBot } = this.getScaleColumnPoints();

		if (!scalePointTop || !scalePointBot) return;

		const distance = Vector3.Distance(scalePointTop, scalePointBot) - deckThickness;
		columnMesh.scaling.y = distance / columnHeight;

		const listMeshes = [];

		const availableColumn = this.getAvailablePostDistanceRule(this.currentPorchStyleID);
		for (let i = 0; i < availableColumn.length; i++) {
			const {
				sides,
				post_distance: distance,
				max_distance: maxDistance,
			} = availableColumn[i];
			sides.forEach((side) => {
				if (!pointPlaces?.[side]) {
					return;
				}
				let startPoint = pointPlaces[side]['start'];
				let endPoint = pointPlaces[side]['end'];

				const totalDistance = Vector3.Distance(startPoint, endPoint);
				const {
					openingSize: opSize,
					openingPosition: opPos,
					openingOffset: opOff,
				} = getOpeningConfigForSide(opening, side);
				let openingSize = opSize;
				let openingPosition = opPos;
				let openingOffset = opOff;

				if (!openingPosition) {
					openingSize = 0;
				}

				const halfColumnWidth: number = columnWidth / 2;
				const transformPoint = getSideBoundaryPoints({
					startPoint,
					endPoint,
					side,
					halfColumnWidth: halfColumnWidth,
					porchStyle: this.currentPorchStyle,
					porchPosition,
				});

				let quantity = calculateComponentQuantity(
					totalDistance,
					distance,
					maxDistance,
					openingSize,
				);

				const { positions } = transformOpeningSettingToColumnPositions({
					opening,
					columnWidth,
					side,
					quantity,
					startPoint: transformPoint.startPoint,
					endPoint: transformPoint.endPoint,
					openingSize,
					openingPosition,
					offset: openingOffset,
					maxDistance,
					distanceColumn: distance,
					originStartPoint: startPoint,
					originEndPoint: endPoint,
				});

				const newPosition = positions.map((position) =>
					position.add(new Vector3(0, deckThickness, 0)),
				);

				const columns = cloneMeshAtPositions(
					newPosition,
					columnMesh,
					`${columnName}_${side}`,
				);
				setColumnInstancesMetadata(columns, side);
				listMeshes.push(...columns);
				if (isSideFixedPosition(this.currentPorchStyle, side)) {
					const defaultLayout = [
						{
							shouldPlace:
								this.currentPorchStyle !== 'corner-left' &&
								(this.currentPorchStyle !== 'corner' || porchPosition === 'right'),
							suffix: 'left',
							point: transformPoint.startPoint,
							braceRotate: 'right',
						},
						{
							shouldPlace:
								this.currentPorchStyle !== 'corner-right' &&
								(this.currentPorchStyle !== 'corner' || porchPosition === 'left'),
							suffix: 'right',
							point: transformPoint.endPoint,
							braceRotate: 'left',
						},
					] as const;

					const lShapeLayout = [
						{
							shouldPlace: true,
							suffix: 'right',
							point: transformPoint.startPoint,
							braceRotate: 'right',
						},
						{
							shouldPlace: true,
							suffix: 'left',
							point: transformPoint.endPoint,
							braceRotate: 'left',
						},
					];

					const layoutOverrides = {
						'deluxe-corner-left': [
							{
								shouldPlace: true,
								suffix: 'right',
								point: transformPoint.startPoint,
								braceRotate: 'right',
							},
							{
								shouldPlace: true,
								suffix: 'left',
								point: transformPoint.endPoint.add(new Vector3(-0.01, 0, 0)),
								braceRotate: 'left',
							},
						],
						'deluxe-porch-flipped': [
							{
								shouldPlace: true,
								suffix: 'right',
								point: transformPoint.startPoint.add(new Vector3(0.01, 0, 0)),
								braceRotate: 'right',
							},
							{
								shouldPlace: true,
								suffix: 'left',
								point: transformPoint.endPoint.add(new Vector3(-0.01, 0, 0)),
								braceRotate: 'left',
							},
						],
						l_shape: lShapeLayout,
						'l-shape-porch': lShapeLayout,
						'l-shape-porch-right': lShapeLayout,
					};

					const fixedColumns = layoutOverrides[this.currentPorchStyle] ?? defaultLayout;

					for (const { shouldPlace, suffix, point, braceRotate } of fixedColumns) {
						if (!shouldPlace) continue;

						const column = columnMesh.createInstance(`${columnName}_${side}_${suffix}`);
						column.position = point.add(new Vector3(0, deckThickness, 0));
						column.metadata = { side, isOutermost: true, braceRotate };
						listMeshes.push(column);
					}
				}
			});
		}
		this.componentMeshes['column'] = listMeshes;
		columnMesh.setEnabled(false);
	};

	/**
	 * Placeholder function for handling brace components. Currently not implemented.
	 */
	handleBrace = () => {
		const currentBraceStyle = this.currentBrace.style;
		const porchPosition = this.currentPorchConfig?.position || 'left';
		this.handleMeshesToScene('brace', currentBraceStyle);
		this.removeComponentMeshes('brace');
		const braceName = this.getMeshNameByTypeStyle('brace', currentBraceStyle);
		const braceMesh = this.getMeshByTypeStyle('brace', currentBraceStyle);

		if (!braceMesh) return;
		braceMesh.setEnabled(true);
		const currentColumnStyle = this.currentColumn.style;
		const columnName = this.getMeshNameByTypeStyle('column', currentColumnStyle);
		const columnMesh = this.getMeshByName(columnName);
		if (!columnMesh) return;

		const listMeshesColumn = this.componentMeshes['column'];
		if (listMeshesColumn.length === 0) return;

		const offsetTopSettings = this.currentBrace.offset_top / 12;
		const columnWidth = Number(helpers.getMeshWidth(columnMesh));

		let { top: pointTop } = this.getScaleColumnPoints();

		pointTop = pointTop.add(new Vector3(0, offsetTopSettings, 0));

		const listMeshes = [];
		for (let i = 0; i < listMeshesColumn.length; i++) {
			const currentColumnMesh = listMeshesColumn[i];
			const { side, isOutermost, braceRotate } = currentColumnMesh.metadata;
			const columnName = currentColumnMesh.name;
			const adjustX = side === 'front' || side === 'back' ? columnWidth / 2 : 0;
			const adjustZ = side === 'left' || side === 'right' ? columnWidth / 2 : 0;
			const braceMeshLeft = braceMesh.createInstance(`${braceName}_${columnName}_left`);
			const braceMeshRight = braceMesh.createInstance(`${braceName}_${columnName}_right`);

			const data = {
				currentColumnMesh,
				pointTop,
				adjustX,
				adjustZ,
				side,
				isOutermost,
				braceRotate,
			};
			const { left, right } = calculateBraceTransform(data);

			braceMeshLeft.position = left.position;
			braceMeshRight.position = right.position;
			braceMeshLeft.rotation = left.rotation;
			braceMeshRight.rotation = right.rotation;
			listMeshes.push(braceMeshLeft, braceMeshRight);
		}
		2;
		this.componentMeshes['brace'] = listMeshes;
		braceMesh.setEnabled(false);
	};

	/**
	 * Draws the ceiling walls using the provided material.
	 * Creates mesh instances for each ceiling wall segment.
	 * @param {object} material - The material information for the ceiling.
	 */
	drawCeilingWall = (material) => {
		const position = this.currentPorchConfig?.position || 'left';
		const walls = getDataDrawWallByType(this.currentPorchStyle, 'ceiling', position);
		const formattedWalls = this.shedBuilder.formatWallInfoFromAPI(walls);
		const ceilingThickness = 0.04;
		const faceUV = createFaceUV();
		formattedWalls.forEach(async (wall) => {
			const { shape, rotation, position } = this.shedBuilder.calculateWallInfo(wall);
			this.shedBuilder._apiData['porch_ceiling_material'].push(wall.materialName);
			const mesh = await this.shedBuilder.drawWall(
				wall.name,
				wall.oldName,
				shape,
				rotation,
				position,
				wall.materialName,
				wall.height,
				wall.parentMesh,
				wall.side,
				ceilingThickness,
				faceUV,
				Mesh.DOUBLESIDE,
			);
			this.ceilingWallMesh.push(mesh);
			applyMaterial({
				mesh,
				materialName: wall.materialName,
				materialInfo: material,
				scene: this.scene,
			});
		});
	};

	/**
	 * Removes all ceiling and deck wall meshes from the scene and disposes of their resources.
	 */
	removeCeilingAndDeck = () => {
		disposeMeshes(this.ceilingWallMesh);
		this.ceilingWallMesh = [];

		disposeMeshes(this.deckWallMesh);
		this.deckWallMesh = [];
	};

	/**
	 * Removes all wall int.
	 */
	removeAllWallDefault = () => {
		const exteriorWalls = this.shedBuilder.getAndFormatExteriorWallBasedOnSetting();
		const interiorWalls = this.shedBuilder.getAndFormatInteriorWall();
		if (!exteriorWalls && !interiorWalls) {
			return [];
		}

		const fullWallInterior = [...exteriorWalls, ...interiorWalls];
		// Filter duplicate wall
		const wallInfoInterior = helpers.filterUniqueItems(
			fullWallInterior,
			'modelVersions',
			this.shedBuilder._shedTypeVersion,
		);
		const _this = this;
		// remove Interior wall before
		wallInfoInterior.forEach((wall) => {
			const wallName = 'interior_' + wall.name;
			const mesh = _this.getMeshByName(wallName);
			const wallNameClone = `${wallName}_clone`;
			const meshClone = _this.getMeshByName(wallNameClone);
			if (mesh) {
				mesh.dispose();
			}
			if (meshClone) {
				meshClone.dispose();
			}
		});

		// remove exterior wall before

		const wallInfoExterior = this.shedBuilder.getWallInfo();
		wallInfoExterior.forEach((wall) => {
			const wallName = wall.name;
			const mesh = _this.getMeshByName(wallName);
			const wallNameClone = `${wallName}_clone`;
			const meshClone = _this.getMeshByName(wallNameClone);
			if (mesh) {
				mesh.dispose();
			}
			if (meshClone) {
				meshClone.dispose();
			}
		});
	};

	/**
	 * Removes all old wall meshes related to the porch from the scene and disposes of their resources, including clones and interior meshes.
	 */
	removeOldWallsPorch = () => {
		this.shedBuilder._apiData['wall_raw'].forEach((wall) => {
			const mesh = this.getMeshByName(wall.name);
			const meshClone = this.getMeshByName(wall.name + '_clone');
			const meshInterior = this.getMeshByName('interior_' + wall.name);
			const meshInteriorClone = this.getMeshByName('interior_' + wall.name + '_clone');
			disposeMeshes([mesh, meshClone, meshInterior, meshInteriorClone].filter(Boolean));
		});
	};

	/**
	 * Removes all corner trim meshes related to the porch from the scene and disposes of their resources.
	 */
	removeCornersTrimPorch = () => {
		this.shedBuilder._apiData['corner_trim'].forEach((trim, i) => {
			const cornerTrimName = `corner_trim_${i}`;
			const cornerTrimMesh = this.getMeshByName(cornerTrimName);
			if (cornerTrimMesh) {
				cornerTrimMesh?.skeleton?.dispose();
				cornerTrimMesh.dispose();
			}
		});
	};

	/**
	 * Draws the deck walls using the provided material and color.
	 * Creates mesh instances for each deck wall segment.
	 * @param {object} material - The material information for the deck.
	 * @param {string} color - The color for the deck.
	 */
	drawDeckWall = (material, color) => {
		const position = this.currentPorchConfig?.position || 'left';
		const walls = getDataDrawWallByType(this.currentPorchStyle, 'deck', position);
		const formattedWalls = this.shedBuilder.formatWallInfoFromAPI(walls);
		const deckThickness = 0.1;
		const faceUV = createFaceUV();

		formattedWalls.forEach(async (wall) => {
			const { shape, rotation, position } = this.shedBuilder.calculateWallInfo(wall);
			this.shedBuilder._apiData['porch_deck_material'].push(wall.materialName);
			const mesh = await this.shedBuilder.drawWall(
				wall.name,
				wall.oldName,
				shape,
				rotation,
				position,
				wall.materialName,
				wall.height,
				wall.parentMesh,
				wall.side,
				deckThickness,
				faceUV,
				Mesh.DOUBLESIDE,
			);
			this.deckWallMesh.push(mesh);
			applyMaterial({
				mesh,
				materialName: wall.materialName,
				materialInfo: material,
				color,
				scene: this.scene,
			});
		});
	};

	/**
	 * Moves main door to a new side when porch style changes.
	 * Steps:
	 * 1. Get the main door from old porch style.
	 * 2. Calculate new position and rotation for the new side.
	 * 3. Place component on new side if position is valid.
	 * 4. Update component data structure and redistribute components.
	 */
	moveDoorToNewSide = (doorInMain: any, allComponents: any, newPorchStyle: string) => {
		const newSide = mainSidePlaceDoor[newPorchStyle];
		const key = doorInMain?.key;
		const { side, parent } = allComponents[key];
		const componentInfo = this.shedBuilder._walls[side]?.items?.[key];
		if (!componentInfo) return;

		const { meshes, name, type, componentWidth, componentHeight, uuid } = componentInfo;

		// Update rotation if side changed
		if (side !== newSide) {
			componentInfo.rotation = this.shedBuilder.calculateComponentRotation(newSide, type);
		}

		const meshesInfo = this.shedBuilder.getWallBones(newSide);
		const args = {
			side: newSide,
			type: type,
			meshesInfo,
			componentWidth,
			componentHeight,
			componentName: name,
			isValidTackRoom: false,
			isTackDoor: true,
			boundingBox: false,
			firstMesh: meshes[0],
		};
		let newPosition = this.shedBuilder.getNewPosition(args);

		if (!newPosition) return;

		componentInfo.position = newPosition;

		// Place component on scene if not a child
		if (parent === null) {
			this.shedBuilder.placeComponent(
				meshes,
				componentInfo.position,
				componentInfo.rotation,
				type,
				newSide,
				name,
			);
		}

		// Move component data to new side structure
		this.shedBuilder.handleMoveComponentToSide(side, newSide, componentInfo);

		// Redistribute components on new side
		if (newSide !== side) {
			this.shedBuilder.multiLineDistributeHorizontal(newSide, true);
		}
	};

	/**
	 * Handles main door transition when porch style changes.
	 * Steps:
	 * 1. Check if main door exists in old porch style.
	 * 2. If valid door found, move it to new porch side.
	 */
	handleMainDoorTransition = (
		allComponents: any,
		oldPorchStyle: string,
		newPorchStyle: string,
	) => {
		const doorInMain: any = getDoorInMainSideOldPorch(allComponents, oldPorchStyle);
		if (!helpers.isEmptyObject(doorInMain)) {
			this.moveDoorToNewSide(doorInMain, allComponents, newPorchStyle);
		}
	};

	/**
	 * Updates component position and moves it to new side.
	 * Steps:
	 * 1. Calculate new position based on wall bones.
	 * 2. Update component info position.
	 * 3. Place component on scene if not a child.
	 * 4. Move component data to new side structure.
	 */
	updateComponentPositionAndSide = (
		componentInfo: any,
		oldSide: string,
		newSide: string,
		isFullWallComponent: boolean,
		isMoveAlongFloor: boolean,
	) => {
		const { position, meshes, name, type, parent } = componentInfo;
		let tempPosition = getAnchorPosition(
			newSide,
			componentInfo,
			this.shedBuilder,
			isMoveAlongFloor,
		);
		componentInfo.rotation = this.shedBuilder.calculateComponentRotation(newSide, type);
		// Update position based on side type
		let newPosition = Vector3.Zero();
		if (!isMoveAlongFloor) {
			if (helpers.hasKeyInArray(zSide, newSide)) {
				const z = isFullWallComponent ? tempPosition.z : position.z;
				newPosition = new Vector3(tempPosition.x, position.y, z);
			} else {
				const x = isFullWallComponent ? tempPosition.x : position.x;
				newPosition = new Vector3(x, position.y, tempPosition.z);
			}
		}

		// Place component on scene if not a child
		if (parent === null) {
			this.shedBuilder.placeComponent(
				meshes,
				newPosition,
				componentInfo.rotation,
				type,
				newSide,
				name,
			);
		}

		// Move component data to new side structure
		this.shedBuilder.handleMoveComponentToSide(oldSide, newSide, componentInfo);
		this.shedBuilder.updateHoleWall(newSide);
	};

	/**
	 * Validates and swaps each component to appropriate new side based on porch style change.
	 * Steps:
	 * 1. Get possible new sides for the component's current side.
	 * 2. Find a valid new side where component fits.
	 * 3. If valid side found, update position and move component.
	 * 4. If no valid side, show notification and delete component.
	 */
	validateAndSwapComponent = (componentKey: string, allComponents: any) => {
		const { side } = allComponents[componentKey];
		const componentInfo = this.shedBuilder._walls[side]?.items?.[componentKey];
		if (!componentInfo) return;
		const { meshes, type, componentSize, isFullWall, parent, name, uuid } = componentInfo;
		if (
			type === 'dormer' ||
			type === 'cupola' ||
			type === 'skylight' ||
			type === 'light' ||
			parent
		) {
			return;
		}
		const isMoveAlongFloor = this.shedBuilder.isMoveAlongFloor(uuid);
		// const anchor: string = this.shedBuilder.getAnchor(type, uuid);
		let anchor: string = this.shedBuilder._componentTypeSettings?.[type]?.['anchor'];
		if (!anchor) {
			const behavior: any = this.shedBuilder.getBehavior(name, type);
			anchor = this.shedBuilder._componentTypeSettings?.[behavior]?.['anchor'];
		}

		const isFullWallComponent =
			(isFullWall || componentSize === COMPONENT_SIZE_FULL_WALL) &&
			(type === 'shelf' || type === 'workbench');

		const newSideFullWall = fullWallComponentMapping?.[this.currentPorchStyle]?.[side] || side;
		const isAllowAddFullWall = isEmptyComponentFullWallOnSide(
			newSideFullWall,
			componentKey,
			allComponents,
		);
		if (isFullWallComponent && newSideFullWall && isAllowAddFullWall) {
			this.shedBuilder.handleMoveComponentToSide(side, newSideFullWall, componentInfo);
			this.shedBuilder.replaceComponentBySize(componentKey);
			window.dispatchEvent(new CustomEvent('update-shed-info'));
		}

		const validNewSide = findValidNewSideForComponent(
			side,
			anchor,
			componentInfo,
			this.shedBuilder,
		);

		if (!validNewSide || isFullWall) {
			const allSides = allSidesPorch?.[this.currentPorchStyle];
			if (isFullWallComponent && allSides.includes(newSideFullWall) && isAllowAddFullWall) {
				this.updateComponentPositionAndSide(
					componentInfo,
					side,
					newSideFullWall,
					isFullWallComponent,
					isMoveAlongFloor,
				);
			} else {
				this.shedBuilder.notification(meshes, this.shedBuilder.NOTICES.itemNotFit);
				this.shedBuilder.deleteComponentV2(side, componentKey, type);
				window.dispatchEvent(new CustomEvent('update-shed-info'));
			}
		} else {
			this.updateComponentPositionAndSide(
				componentInfo,
				side,
				validNewSide,
				isFullWallComponent,
				isMoveAlongFloor,
			);
		}
	};

	changePositionForRamp = (componentInfo, parentSide) => {
		const { side, meshes, type, parent } = componentInfo;
		const isChangePositionRamp = this.isChangePositionRamp(this.currentPorchStyle, parentSide);
		let z = 0;
		if (isChangePositionRamp) {
			const { depth, width, alley_width, side_wall_width } = this.currentPorchConfig;
			if (this.currentPorchStyle === 'l_shape' && parentSide === 'inner_front') {
				z = Number(side_wall_width) + Number(depth);
			} else if (parentSide === 'inner_front' || parentSide === 'inner_back') {
				z = width;
			} else {
				z = depth;
			}
		}
		meshes[0].position = new Vector3(meshes[0].position.x, meshes[0].position.y, z);
	};

	/**
	 * Swaps all components from one side of the model to the corresponding opposite side.
	 * Used when flipping or mirroring a building model (e.g., left ↔ right).
	 *
	 * @param {Object} allComponents - A collection of all components currently in the model.
	 * Each component entry should include:
	 *   - side {string}: The current side of the component (e.g., "left", "right").
	 *   - parent {any}: The parent object (if any). If null, the component is directly placed in the scene.
	 *
	 * This method will:
	 *   1. Determine the new side configuration based on the flip mode.
	 *   2. Update component position and rotation accordingly.
	 *   3. Move the component data reference to the new side in the internal `_walls` structure.
	 */
	swapComponentSideToSide = (porchStyle, allComponents) => {
		const sidesMap = getSidesFlipModelOption(porchStyle);

		for (const key in allComponents) {
			const { side, parent } = allComponents[key];
			const newSide = convertSides(sidesMap, side);
			const componentInfo = this.shedBuilder._walls[side]?.items?.[key];
			if (!componentInfo) {
				continue;
			}

			const {
				position,
				meshes,
				name,
				type,
				componentWidth,
				componentHeight,
				isFullWall,
				componentSize,
				uuid,
			} = componentInfo;

			if (
				type === 'dormer' ||
				type === 'cupola' ||
				type === 'skylight' ||
				type === 'light' ||
				parent
			) {
				continue;
			}
			const isMoveAlongFloor = this.shedBuilder.isMoveAlongFloor(uuid);
			// const anchor: string = this.shedBuilder.getAnchor(type, uuid);
			let anchor: string = this.shedBuilder._componentTypeSettings?.[type]?.['anchor'];
			if (!anchor) {
				const behavior: any = this.shedBuilder.getBehavior(name, type);
				anchor = this.shedBuilder._componentTypeSettings?.[behavior]?.['anchor'];
			}

			// Update rotation if side changes

			const isFullWallComponent =
				(isFullWall || componentSize === COMPONENT_SIZE_FULL_WALL) &&
				(type === 'shelf' || type === 'workbench');

			if (isFullWallComponent) {
				this.shedBuilder.handleMoveComponentToSide(side, newSide, componentInfo);
				this.shedBuilder.replaceComponentBySize(key);
				window.dispatchEvent(new CustomEvent('update-shed-info'));
				this.updateComponentPositionAndSide(
					componentInfo,
					side,
					newSide,
					isFullWallComponent,
					isMoveAlongFloor,
				);
			} else {
				if (side !== newSide) {
					componentInfo.rotation = this.shedBuilder.calculateComponentRotation(
						newSide,
						type,
					);
				}
				const anchorPosition = getAnchorPosition(
					newSide,
					componentInfo,
					this.shedBuilder,
					isMoveAlongFloor,
				);

				const newPosition = getFlippedPositionForComponent(
					porchStyle,
					side,
					position,
					anchorPosition,
				);
				const args = {
					side: newSide,
					targetPosition: newPosition,
					component: componentInfo,
					componentWidth: componentWidth,
					componentHeight: componentHeight,
					anchor,
				};
				const isInside = this.shedBuilder.isComponentInSideMeshes(args);
				if (isInside) {
					// Update position after flipping
					componentInfo.position = newPosition;

					// Re-place top-level components only (no parent)
					if (parent === null) {
						this.shedBuilder.placeComponent(
							meshes,
							componentInfo.position,
							componentInfo.rotation,
							type,
							newSide,
							name,
						);
					}

					// Move the component data to the new side structure
					this.shedBuilder.handleMoveComponentToSide(side, newSide, componentInfo);
					this.shedBuilder.updateHoleWall(newSide);
				} else {
					this.shedBuilder.notification(meshes, this.shedBuilder.NOTICES.itemNotFit);
					this.shedBuilder.deleteComponentV2(side, key, type);
					window.dispatchEvent(new CustomEvent('update-shed-info'));
				}
			}
		}
	};

	/**
	 * Handles swapping components when porch style changes.
	 * Processes main door transition first, then validates and swaps all other components.
	 * Steps:
	 * 1. Return early if no components exist.
	 * 2. Handle main door transition if needed (from old to new porch style).
	 * 3. Validate and swap all components to appropriate new sides.
	 */
	handleSwapComponent = (allComponents, oldPorchConfig, currentPorchConfig) => {
		if (helpers.isEmptyObject(allComponents)) return;

		// Handle main door transition from old porch style to new
		// if (
		// 	oldPorchStyle === this.PORCH_STYLE_NONE ||
		// 	!isHasDoorInMain(allComponents, newPorchStyle)
		// ) {
		// 	this.handleMainDoorTransition(allComponents, oldPorchStyle, newPorchStyle);
		// }

		const { style: oldPorchStyle, position: oldPorchPosition, id: oldPorchId } = oldPorchConfig;
		const {
			style: newPorchStyle,
			position: newPorchPosition,
			id: newPorchId,
		} = currentPorchConfig;
		if (
			oldPorchStyle === newPorchStyle &&
			oldPorchPosition === newPorchPosition &&
			newPorchId !== oldPorchId
		) {
			const newSize = this.shedBuilder.getSizeGroup();
			const oldSize = this.shedBuilder.updateOldSize(newSize);
			this.shedBuilder.placeAllComponents(oldSize, newSize, false);
		}

		const isFlipComponent =
			newPorchStyle === 'corner' || newPorchStyle === 'deluxe' || newPorchStyle === 'l_shape';

		// Validate and swap all components to appropriate sides
		if (
			isFlipComponent &&
			oldPorchStyle === newPorchStyle &&
			oldPorchPosition !== newPorchPosition &&
			newPorchId !== oldPorchId
		) {
			this.swapComponentSideToSide(newPorchStyle, allComponents);
		} else {
			for (const key in allComponents) {
				this.validateAndSwapComponent(key, allComponents);
			}
		}

		// update position detail for child component
		for (const key in allComponents) {
			// update position for ramp component
			const { side, type } = allComponents[key];
			const componentInfo = this.shedBuilder._walls[side]?.items?.[key];
			const parent = componentInfo?.parent;
			if (parent) {
				const { side: parentSide } = allComponents[parent];
				if (type === 'ramp') {
					this.changePositionForRamp(componentInfo, parentSide);
				}
				this.shedBuilder.handleMoveComponentToSide(side, parentSide, componentInfo);
			}
		}

		this.shedBuilder.isValidAllComponents();
	};

	/**
	 * Initializes the color for post and rail components based on the selected option.
	 * Triggers texture change animation for the rail material.
	 */
	initColorPostRail = () => {
		const element = jQuery('.ssb-configurator-color.rails .ssb-option.active');
		if (element.length) {
			let materialName = element.attr('data-material');
			const textureType = element.attr('data-texture-type');
			const textureImage = element.attr('data-texture-image');
			const textureColor = element.attr('data-texture-color');
			this.shedBuilder.animateTextureChange(
				materialName,
				textureType,
				textureImage,
				textureColor,
			);
		} else {
			this.shedBuilder.updatePostColor();
		}
	};

	/**
	 * Gets the list of sides where a specific component type can be added based on the current porch style and configuration.
	 * @param {string} side - The side of the porch (e.g., 'front', 'left').
	 * @param {string} componentType - The type of component (e.g., 'door').
	 * @param {boolean} isInterior - Indicates if the component is for an interior space.
	 * @returns {Array} List of sides where the component can be added.
	 */
	getSidesAddComponent = (side, componentType, isInterior) => {
		const porchPosition = this.currentPorchConfig?.position || 'left';
		const porchStyle = this.currentPorchStyle;
		let sides = [];
		if (porchStyle === 'front' || porchStyle === 'middle' || porchStyle === 'side') {
			sides = sidesPorchConfig[porchStyle]?.[side] || [];
		} else if (porchStyle === 'corner') {
			sides = sidesPorchConfig[porchStyle]?.[porchPosition]?.[side] || [];
		} else if (porchStyle === 'l_shape') {
			sides = sidesPorchConfig[porchStyle]?.[porchPosition]?.[side] || [];
		}
		if (componentType === 'door' || isInterior) {
			sides = sides.filter((s) => s !== 'porch');
		}

		return sides;
	};

	/**
	 * Creates the skeleton for the porch body and initializes bones based on the skeleton mapping configuration.
	 */
	createSkeleton = () => {
		const porchBodySkeleton = new Skeleton('bodyPorch', 'bodyPorch', this.scene);
		const bones = [];
		for (const [key, value] of Object.entries(skeletonMapping)) {
			if (!this.shedBuilder._bones[key]) {
				const bone = new Bone(key, porchBodySkeleton, null);
				this.shedBuilder._bones[key] = bone;
			}
		}
	};

	/**
	 * Check Ramp need change position
	 */
	isChangePositionRamp = (porchStyle: string, side: string) => {
		if (
			(porchStyle === 'front' && side === 'front') ||
			(porchStyle === 'side' && (side === 'left' || side === 'front_inner_porch')) ||
			(porchStyle === 'middle' && side === 'front_inner_porch') ||
			(porchStyle === 'corner' &&
				(side === 'inner_left' || side === 'inner_front' || side === 'inner_back')) ||
			(porchStyle === 'l_shape' &&
				(side === 'inner_left' ||
					side === 'inner_right' ||
					side === 'inner_front' ||
					side === 'front'))
		) {
			return true;
		}
		return false;
	};

	// isContinueHandleColumn = (openingSize: number, distance: number) => {
	// 	if (openingSize)
	// }

	/* Read the currently selected porch preset data and apply the corresponding UI state:
	 * - Show/hide the "Porch Railing" sub-section based on railing_user_toggle
	 * - Set the default railing radio button based on railing_default_on
	 * - Enable/disable opening tabs and fields based on preset sides configuration
	 */
	applyPorchPresetState = (selectedPreset, presetData): void => {
		const selectedParent = selectedPreset.closest('div');

		// Move sub-sections right below the selected preset radio button
		jQuery(PORCH_PRESET.railingGroup).insertAfter(selectedParent);
		jQuery(PORCH_PRESET.openingGroup).insertAfter(jQuery(PORCH_PRESET.railingGroup));

		// Set the default railing radio button based on railing_default_on
		const railingValue = presetData.railing_default_on ? 'with-railing' : 'no-railing';
		jQuery(`${PORCH_PRESET.presetRailingName}[value="${railingValue}"]`).prop('checked', true);

		// Show/hide railing sub-section based on railing_user_toggle
		if (presetData.railing_user_toggle) {
			jQuery(PORCH_PRESET.railingGroup).show();
		} else {
			jQuery(PORCH_PRESET.railingGroup).hide();
		}

		// Update Custom Opening tabs and fields based on selected preset
		const sides = Array.isArray(presetData.sides) ? presetData.sides : [];
		let hasEnabledSides = false;
		let firstEnabledSide: string | null = null;

		jQuery(PORCH_PRESET.openingGroup)
			.find(PORCH_PRESET.openingPanel)
			.each(function () {
				const currentPannel = jQuery(this);
				const panelSide = currentPannel.attr('data-side');
				const sideConfig = sides.find((s: any) => s.value === panelSide);
				const tabButton = jQuery(PORCH_PRESET.openingGroup).find(
					`${PORCH_PRESET.openingTab}[data-side="${panelSide}"]`,
				);

				const allowCustom =
					sideConfig && Array.isArray(sideConfig.allow_custom_opening)
						? sideConfig.allow_custom_opening
						: [];
				const isEditable =
					sideConfig && sideConfig.opening_enabled && allowCustom.length > 0;

				// Populate default/configured values if config is present
				if (sideConfig) {
					const openingData =
						Array.isArray(sideConfig.opening_data) && sideConfig.opening_data.length > 0
							? sideConfig.opening_data[0]
							: { size: 0, offset: 0, position: 'middle', size_type: 'fixed' };

					// Size field
					currentPannel
						.find(PORCH_PRESET.openingSize)
						.val(openingData.size)
						.prop('disabled', !allowCustom.includes('size'));

					// Size type
					const sizeType = openingData.size_type === 'percent' ? 'percent' : 'fixed';
					currentPannel.attr('data-size-type', sizeType);

					// Position select
					const positionSelect = currentPannel.find(PORCH_PRESET.openingPosition);
					positionSelect
						.val(openingData.position)
						.prop('disabled', !allowCustom.includes('position'));

					// Enable/disable individual position options
					const availablePositions = Array.isArray(sideConfig.available_opening_positions)
						? sideConfig.available_opening_positions
						: [];
					positionSelect.find('option').each(function () {
						const optVal = currentPannel.val() as string;
						currentPannel.prop('disabled', !availablePositions.includes(optVal));
					});

					// Offset field
					currentPannel
						.find(PORCH_PRESET.openingOffset)
						.val(openingData.offset)
						.prop(
							'disabled',
							!allowCustom.includes('offset') || openingData.position === 'middle',
						);

					// DOM: Switch input constraints and unit labels by size type: percent (0-100, %) vs fixed (ft / inch)
					const sizeInput = currentPannel.find(PORCH_PRESET.openingSize);
					const offsetInput = currentPannel.find(PORCH_PRESET.openingOffset);
					if (sizeType === 'percent') {
						sizeInput.attr({ max: 100, step: 1 });
						offsetInput.attr({ max: 100, step: 1 });
						currentPannel.find('.opening-size-unit').text('(%)');
						currentPannel.find('.opening-offset-unit').text('(%)');
					} else {
						sizeInput.removeAttr('max').attr('step', 0.5);
						offsetInput.removeAttr('max').attr('step', 1);
						currentPannel.find('.opening-size-unit').text('(ft)');
						currentPannel.find('.opening-offset-unit').text('(inch)');
					}
				}

				if (!isEditable) {
					// Mark tab and panel as non-editable (hidden via CSS)
					tabButton.removeClass(
						`${PORCH_PRESET.isEditableClass} ${PORCH_PRESET.activeClass}`,
					);
					currentPannel.removeClass(
						`${PORCH_PRESET.isEditableClass} ${PORCH_PRESET.activeClass}`,
					);
					currentPannel.find('input, select').prop('disabled', true);
				} else {
					hasEnabledSides = true;
					// Track the first editable side for activation later
					if (!firstEnabledSide) {
						firstEnabledSide = panelSide;
					}
					// Mark tab and panel as editable (visible via CSS)
					tabButton.addClass(PORCH_PRESET.isEditableClass);
					currentPannel.addClass(PORCH_PRESET.isEditableClass);
				}
			});

		// Hide the entire opening group if no sides are enabled
		if (!hasEnabledSides) {
			jQuery(PORCH_PRESET.openingGroup).hide();
		} else {
			jQuery(PORCH_PRESET.openingGroup).show();
			// Check if the currently active tab is still editable
			const activeTab = jQuery(PORCH_PRESET.openingGroup).find(
				`${PORCH_PRESET.openingTab}.${PORCH_PRESET.isEditableClass}.${PORCH_PRESET.activeClass}`,
			);

			if (activeTab.length === 0 && firstEnabledSide) {
				// Activate the first editable tab and its corresponding panel
				jQuery(PORCH_PRESET.openingGroup)
					.find(PORCH_PRESET.openingTab)
					.removeClass(PORCH_PRESET.activeClass);
				jQuery(PORCH_PRESET.openingGroup)
					.find(`${PORCH_PRESET.openingTab}[data-side="${firstEnabledSide}"]`)
					.addClass(PORCH_PRESET.activeClass);
				jQuery(PORCH_PRESET.openingGroup)
					.find(PORCH_PRESET.openingPanel)
					.removeClass(PORCH_PRESET.activeClass);
				jQuery(PORCH_PRESET.openingGroup)
					.find(`${PORCH_PRESET.openingPanel}[data-side="${firstEnabledSide}"]`)
					.addClass(PORCH_PRESET.activeClass);
			} else if (activeTab.length > 0) {
				// Ensure the corresponding panel is active
				const side = activeTab.data('side');
				jQuery(PORCH_PRESET.openingGroup)
					.find(PORCH_PRESET.openingPanel)
					.removeClass(PORCH_PRESET.activeClass);
				jQuery(PORCH_PRESET.openingGroup)
					.find(`${PORCH_PRESET.openingPanel}[data-side="${side}"]`)
					.addClass(PORCH_PRESET.activeClass);
			}
		}
	};

	/**
	 * Handle tab click events in the Custom Opening section.
	 * Switches the active tab and shows the corresponding panel.
	 *
	 * @param {Event} e - The click event
	 */
	handlePorchPresetOpeningTabs = (e): void => {
		const clickedTab = jQuery(e.currentTarget);
		// Only allow clicking on editable tabs
		if (
			!clickedTab.hasClass(PORCH_PRESET.isEditableClass) ||
			clickedTab.hasClass(PORCH_PRESET.activeClass)
		) {
			return;
		}

		const side = clickedTab.data('side');
		const container = clickedTab.closest(PORCH_PRESET.openingGroup);

		// Switch active tab
		container.find(PORCH_PRESET.openingTab).removeClass(PORCH_PRESET.activeClass);
		clickedTab.addClass(PORCH_PRESET.activeClass);

		// Switch active panel
		container.find(PORCH_PRESET.openingPanel).removeClass(PORCH_PRESET.activeClass);
		container
			.find(`${PORCH_PRESET.openingPanel}[data-side="${side}"]`)
			.addClass(PORCH_PRESET.activeClass);
	};

	/**
	 * Get preset openings
	 *
	 * @returns {position: string;size: number;side: string;offset: number}[]
	 */
	getPresetOpenings = (
		currentPorchPreset,
	): {
		position: string;
		size: number;
		side: string;
		offset: number;
		sizeType: string;
		rawSize: number;
		rawOffset: number;
	}[] => {
		const openings = [];
		const presetSides =
			currentPorchPreset && Array.isArray(currentPorchPreset.sides)
				? currentPorchPreset.sides
				: [];
		const _this = this;

		jQuery(PORCH_PRESET.openingGroup)
			.find(PORCH_PRESET.openingPanel)
			.each(function () {
				const currentPannel = jQuery(this);
				const side = currentPannel.attr('data-side');
				const sideConfig = presetSides.find((s: any) => s.value === side);
				const openingData =
					sideConfig &&
					Array.isArray(sideConfig.opening_data) &&
					sideConfig.opening_data.length > 0
						? sideConfig.opening_data[0]
						: { size: 0, offset: 0, position: 'middle' };

				const sizeInput = currentPannel.find(PORCH_PRESET.openingSize);
				const positionSelect = currentPannel.find(PORCH_PRESET.openingPosition);
				const offsetInput = currentPannel.find(PORCH_PRESET.openingOffset);

				// Priotize attr from applyPorchPresetState
				const sizeType =
					currentPannel.attr('data-size-type') || openingData.size_type || 'fixed';

				// If field is disabled, fallback to config data. Otherwise read from DOM.
				const rawSize = sizeInput.prop('disabled')
					? Number(openingData.size) || 0
					: Number(sizeInput.val()) || 0;

				const position = positionSelect.prop('disabled')
					? openingData.position || 'middle'
					: (positionSelect.val() as string) || 'middle';

				const rawOffset = offsetInput.prop('disabled')
					? Number(openingData.offset) || 0
					: Number(offsetInput.val()) || 0;

				const porchPosition = _this.currentPorchConfig?.position ?? 'left';
				const pointPlaces = _this.getPointPlaceByType(
					_this.currentPorchStyle,
					porchPosition,
				);
				if (!pointPlaces?.[side]) {
					return;
				}
				let startPoint = pointPlaces[side]['start'];
				let endPoint = pointPlaces[side]['end'];
				const totalDistance = Vector3.Distance(startPoint, endPoint);
				const sideLength = totalDistance;
				const { sizeFt, offsetFt } = resolveOpeningToFeet(
					sizeType,
					rawSize,
					rawOffset,
					sideLength,
				);

				openings.push({
					position,
					size: sizeFt,
					side,
					offset: offsetFt,
					sizeType,
					rawSize,
					rawOffset,
				});
			});

		return openings;
	};
}
